<script setup lang="ts">
    import BaseNavbarMenuItems from "@/components/Base/BaseNavbar/BaseNavbarMenuItems.vue"
    import checkPermissions from "@/utils/checkPermissions"
    import { useUserStore } from "@/stores/user"
    import type { RouteRecordRaw } from "vue-router"

    interface NavbarRouteMeta {
        hasSubMenuGroup?: boolean
        subMenuGroup?: string
        subGroupName?: string
        subMenuGroupIndex?: number
        subIndex?: number
        isSubShow?: boolean
        permissions?: string[]
    }

    interface FormattedMenuItem {
        hasSubMenuGroup?: boolean
        subMenuGroup?: string
        item?: RouteRecordRaw
        subMenuGroupIndex?: number
        type?: string
        items?: RouteRecordRaw[]
    }

    defineProps<{
        items: RouteRecordRaw[]
    }>()

    const userStore = useUserStore()

    function formatItems(items: RouteRecordRaw[]) {
        const groups: Record<
            string,
            { type: string; items: RouteRecordRaw[] }
        > = {}

        return (
            items?.reduce<FormattedMenuItem[]>((result, item) => {
                const meta = item.meta as NavbarRouteMeta | undefined
                if (!meta) return result

                if (!meta.hasSubMenuGroup) {
                    result.push({
                        hasSubMenuGroup: meta.hasSubMenuGroup,
                        subMenuGroup: meta.subMenuGroup,
                        item: item,
                    })
                } else {
                    const subMenuGroup = meta.subMenuGroup || ""
                    if (!groups[subMenuGroup]) {
                        groups[subMenuGroup] = {
                            type: subMenuGroup,
                            items: [],
                        }
                        result.push({
                            subMenuGroupIndex: meta.subMenuGroupIndex,
                            hasSubMenuGroup: meta.hasSubMenuGroup,
                            subMenuGroup: subMenuGroup,
                            ...groups[subMenuGroup],
                        })
                    }
                    groups[subMenuGroup].items.push(item)
                }
                return result
            }, []) || []
        )
    }

    function getMenuItems(
        parentMenuItem: RouteRecordRaw,
        userStore: ReturnType<typeof useUserStore>
    ) {
        return (
            parentMenuItem.children
                ?.filter(
                    (item) =>
                        (item.meta as NavbarRouteMeta | undefined)?.isSubShow
                )
                .filter((item) => {
                    return checkPermissions(
                        (item.meta as NavbarRouteMeta | undefined)?.permissions,
                        userStore
                    )
                })
                .slice()
                .sort(
                    (a, b) =>
                        ((a.meta as NavbarRouteMeta | undefined)?.subIndex ??
                            0) -
                        ((b.meta as NavbarRouteMeta | undefined)?.subIndex ?? 0)
                ) || []
        )
    }
</script>
<template>
    <template v-if="items.length">
        <template v-for="(formatItem, index) of formatItems(items)">
            <el-menu-item
                v-if="
                    !formatItem.hasSubMenuGroup ||
                    !formatItem.items ||
                    formatItem.items?.length === 0
                "
                :key="'item' + index"
                data-test="sidebar-item"
                :index="String((formatItem.item?.meta as NavbarRouteMeta)?.subIndex ?? '')"
                @click="
                    () =>
                        formatItem.item &&
                        $router.push({ name: formatItem.item.name })
                "
            >
                <template #title>
                    {{
                        (formatItem.item?.meta as NavbarRouteMeta)?.subGroupName
                    }}
                </template>
            </el-menu-item>
            <el-sub-menu
                v-else
                :key="'group' + index"
                :index="String(formatItem.subMenuGroupIndex ?? '')"
                class="custom-sub-menu"
            >
                <template #title>
                    <span>{{ formatItem.subMenuGroup }}</span>
                </template>
                <el-menu-item-group>
                    <template
                        v-for="(formatSubItem, subIndex) of formatItem.items"
                        :key="subIndex"
                    >
                        <BaseNavbarMenuItems
                            v-if="getMenuItems(formatSubItem, userStore)"
                            :items="getMenuItems(formatSubItem, userStore)"
                        ></BaseNavbarMenuItems>
                    </template>
                </el-menu-item-group>
            </el-sub-menu>
        </template>
    </template>
</template>

<style scoped>
    .el-sub-menu.is-opened
        :deep(> .el-sub-menu__title > .el-sub-menu__icon-arrow) {
        @apply !rotate-0;
    }

    .el-sub-menu :deep(.el-sub-menu__icon-arrow) {
        @apply !-rotate-90;
    }

    :deep(.el-sub-menu__title .el-sub-menu__icon-arrow) {
        @apply right-[unset] left-2.5;
    }

    :deep(.custom-sub-menu.el-sub-menu.is-opened .el-sub-menu__title > span) {
        @apply opacity-50;
    }

    :deep(.custom-sub-menu.el-sub-menu .el-sub-menu__icon-arrow) {
        @apply hidden;
    }

    :deep(.el-menu-item-group__title) {
        padding: 0;
    }
</style>
